import { Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import { Action } from '../../actions';
import { Api } from '../../typings/api';
import { DriveItemInfo } from '../DriveItemList/reducer';
import { getOrFetchDriveItemMeta, updateDriveItem } from '../DriveItemList/actions';
import UserAgentUtils from '../../utils/userAgent';
import ApiUtils from '../../utils/api';
import { addPendingShare } from '../PendingTasksManager/actions';
import { createNotification } from '../NotificationBar/actions';
import UrlUtils from '../../utils/url';
import Utils from '../../utils';
import StatsUtils from '../../utils/statistics';
import NativeApi from '../../utils/nativeApi';
import { AppType } from '../App/reducer';
import DriveItemUtils from '../../utils/driveItem';
import { finishProposalSharing } from '../ShareProposal/actions';
import { FormField } from '../../components/Share/UprezForm/typings';

enum ShareOrigin {
	Detail = '@@ShareOrigin/detail',
	Theater = '@@ShareOrigin/theater',
	Selection = '@@ShareOrigin/selection',
}

enum SharePanelActionKeys {
	OPEN_PANEL = '@@sharePanel/OPEN_PANEL',
	CLOSE_PANEL = '@@sharePanel/CLOSE_PANEL',
	START_PROCESSING = '@@sharePanel/START_PROCESSING',
	FINISH_PROCESSING = '@@sharePanel/FINISH_PROCESSING',
	SET_SELECTION = '@@sharePanel/SET_SELECTION',
	SET_METHOD = '@@sharePanel/SET_METHOD',
	SET_FORM_FIELD = '@@sharePanel/SET_FORM_FIELD',
	RESET_FORM = '@@sharePanel/RESET_FORM',
	UPDATE_FOLDERS_CONTENT = '@@sharePanel/UPDATE_FOLDERS_CONTENT',
	SET_CRM_CONTACTS = '@@sharePanel/SET_CRM_CONTACTS'
}

interface OpenPanelAction extends Action {
	type: SharePanelActionKeys.OPEN_PANEL;
	isProposalShare: boolean;
}

interface StartProcessingAction extends Action {
	type: SharePanelActionKeys.START_PROCESSING;
}

interface FinishProcessingAction extends Action {
	type: SharePanelActionKeys.FINISH_PROCESSING;
}

interface ClosePanelAction extends Action {
	type: SharePanelActionKeys.CLOSE_PANEL;
}

interface ResetFormAction extends Action {
	type: SharePanelActionKeys.RESET_FORM;
}

interface SetSelectionAction extends Action {
	type: SharePanelActionKeys.SET_SELECTION;
	selection: Api.DriveItem[];
}

interface SetFormFieldAction extends Action {
	type: SharePanelActionKeys.SET_FORM_FIELD;
	name: string;
	value: string;
}

interface UpdateFoldersContentAction extends Action {
	type: SharePanelActionKeys.UPDATE_FOLDERS_CONTENT;
	item: Api.DriveItem;
}

interface SetCrmContactsAction extends Action {
	type: SharePanelActionKeys.SET_CRM_CONTACTS;
	crmContacts: Api.CrmContact[];
}

type SharePanelAction = OpenPanelAction
	| ClosePanelAction
	| StartProcessingAction
	| FinishProcessingAction
	| ResetFormAction
	| SetSelectionAction
	| SetFormFieldAction
	| UpdateFoldersContentAction
	| SetCrmContactsAction;

const share = (sharedItems: Api.DriveItem[], isProposalShare = false) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const { appInfos, galleryInfos, authentication } = getState();
	const { needCredentials } = authentication.userInfos;
	const { config: galleryConfig } = galleryInfos;
	const type = appInfos.type || AppType.Single;
	const isNativeShare = UserAgentUtils.isMobileApp && !Utils.isSecure(type, needCredentials);

	StatsUtils.trackShare(sharedItems);

	if (isNativeShare) {
		const title = DriveItemUtils.getShareTitle(sharedItems);
		const url = DriveItemUtils.getShareLink(sharedItems, galleryConfig, ShareOrigin.Selection);
		NativeApi.shareApp(title, url);
	} else {
		dispatch(initiateShare(sharedItems, isProposalShare));
	}
};

const initiateShare = (sharedItems: Api.DriveItem[], isProposalShare: boolean) => async (dispatch: Dispatch<StoreState>) => {
	dispatch({
		type: SharePanelActionKeys.SET_SELECTION,
		selection: sharedItems,
	});

	dispatch({
		type: SharePanelActionKeys.OPEN_PANEL,
		isProposalShare,
	});

	dispatch({ type: SharePanelActionKeys.START_PROCESSING });

	for (let i = 0; i < sharedItems.length; i++) {
		const item = sharedItems[i];
		if (item.type === Api.DriveItemType.Directory) {
			await dispatch(retrieveSubFoldersContent(item.id));
		}
	}

	dispatch({ type: SharePanelActionKeys.FINISH_PROCESSING });
};

const finishShare = () => (dispatch: Dispatch<StoreState>) => {
	dispatch({ type: SharePanelActionKeys.RESET_FORM });
	dispatch({ type: SharePanelActionKeys.CLOSE_PANEL });
	dispatch({ type: SharePanelActionKeys.FINISH_PROCESSING });
};

const getContactInfo = (isMultiApp: boolean, hasMultipleContacts: boolean) =>
	(dispatch: Dispatch<StoreState>, getState: () => StoreState): Api.SharedContactInfo | {} | undefined => {
	const { form } = getState().sharePanel;
	const { contactEmail, company, firstname, phone, position, surname } = form;
	if (!isMultiApp) {
		return undefined;
	} else if (hasMultipleContacts) {
		return {};
	} else {
		return {
			company,
			forename: firstname,
			email: contactEmail,
			name: surname,
			phone,
			position
		};
	}
};

const shareItems = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const {
		appInfos,
		sharePanel,
		multiAppAuthenticationInfos,
		i18n: { locale },
		driveList: { driveItems },
	} = getState();
	const { form, isProposalShare } = sharePanel;
	const { credentials } = multiAppAuthenticationInfos;
	const { userName, userEmail, contactEmail, message, subject, createDeal } = form;
	const isMultiApp = Utils.isMultiApp(appInfos);
	const resources = await dispatch(retrieveResources());

	// Sending contactInfo is how backend differenciate between SingleApp and MultiApp share
	// In SingleApp it needs to be undefined (subject is also inexistant in SingleApp)
	// In MultiApp, if there is only one recipient the only property that is required in "contactInfo" is "email"
	// If there is multiple recipients, contactInfo must be an empty object
	// In both cases, "toAddresses" is the only required field by the API

	const emailsArray = contactEmail
		.split(/[ ,]+/)
		.filter(item => item !== '');
	const hasInvalidEmail = emailsArray.some(item => !Utils.isValidEmail(item));

	if (hasInvalidEmail) {
		dispatch(createNotification('error.invalid_email_address'));
		return;
	}

	const contactInfo = await dispatch(getContactInfo(isMultiApp, emailsArray.length > 1));

	let requestData: Api.SharedData = {
		fromName: !isMultiApp ? userName : '',
		fromEmail: isMultiApp && credentials && credentials.login ? credentials.login : userEmail,
		toAddresses: emailsArray,
		message,
		language: locale,
		resources,
		contactInfo,
		subject: isMultiApp ? subject : undefined,
		createDeal,
	};

	const isOnline = await UserAgentUtils.isOnlinePromise();

	if (isOnline) {
		dispatch({ type: SharePanelActionKeys.START_PROCESSING });
		ApiUtils.shareContent(requestData)
			.then(response => {
				// If an error occured, the server still return a success
				// But the response is KO
				if (response === 'response=ko') {
					return Promise.reject();
				}
				return Promise.resolve();
			})
			.then(() => {
				dispatch(finishShare());

				if (isProposalShare) {
					dispatch(finishProposalSharing(resources[0].id));
				} else {
					// change status of shared proposals
					const draftProposalsIds = resources
						.map(({ id }) => id)
						.filter(id => DriveItemUtils.isDraftProposal(driveItems[id]));

					for (let i = 0; i < draftProposalsIds.length; i++) {
						ApiUtils.getMeta(draftProposalsIds[i])
							.then(item => dispatch(updateDriveItem(item)));
					}
				}
			})
			.catch(() => {
				const errorMessage = isProposalShare
					? 'error.share_proposal_error'
					: 'error.share_failed_request';
				dispatch(createNotification(errorMessage));
				dispatch({ type: SharePanelActionKeys.FINISH_PROCESSING });
			});
	} else {
		dispatch(addPendingShare(requestData));
		dispatch(createNotification('info.selection_shared_once_online'));
		dispatch(finishShare());
	}
};

const retrieveResources = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const { galleryInfos, sharePanel } = getState();
	const galleryConfig = galleryInfos.config;
	const { selection, foldersContent } = sharePanel;
	const sharedContent = [...selection.filter(item => item.type !== Api.DriveItemType.Directory), ...foldersContent];

	return sharedContent.map(item => {
		const { type, id, originalFile, src } = item;
		let url = '';

		switch (type) {
			case Api.DriveItemType.Gallery:
			case Api.DriveItemType.AppContainer:
				url = `${UrlUtils.getConfiguredRootApiClient(galleryConfig)}/${id}`;
				break;
			case Api.DriveItemType.Publication:
				url = UrlUtils.getPublicationUrl(galleryConfig, item, 1, false, true);
				break;
			case Api.DriveItemType.ExternalLink:
				const linkId = src.replace('$media$', '');
				url = `${UrlUtils.getConfiguredRootApiClient(galleryConfig)}/${linkId}`;
				break;
			default:
				url = `${UrlUtils.getConfiguredRootApiClient(galleryConfig)}/${originalFile}`;
				break;
		}

		return { id, url };
	});
};

const addSubFoldersContent = (item: Api.DriveItem) => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const { sharePanel } = getState();
	const { selection } = sharePanel;

	if (item.id && !selection.some(parent => parent.id === item.id)) {
		dispatch({
			type: SharePanelActionKeys.UPDATE_FOLDERS_CONTENT,
			item,
		});
	}
};

const setCrmContacts = (crmContacts: Api.CrmContact[]) => (dispatch: Dispatch<StoreState>) => {
	const action: SetCrmContactsAction = {
		type: SharePanelActionKeys.SET_CRM_CONTACTS,
		crmContacts,
	};
	dispatch(action);
};

const contactKeyByFieldName = {
	[FormField.Firstname]: 'firstName',
	[FormField.Surname]: 'lastName',
	[FormField.Email]: 'email',
	[FormField.Company]: 'company',
	[FormField.Position]: 'position',
	[FormField.Phone]: 'phone',
};

const setFormFieldByContactField = (name: FormField, value: string | null) => (dispatch: Dispatch<StoreState>) => {
	const action: SetFormFieldAction = {
		type: SharePanelActionKeys.SET_FORM_FIELD,
		name,
		value: value || '',
	};
	dispatch(action);
};

const setFormFieldsByContact = (crmContact: Api.CrmContact) => (dispatch: Dispatch<StoreState>) => {
	const formKeys = Object.keys(contactKeyByFieldName) as FormField[];
	formKeys.forEach((formKey) => {
		const contactKey = contactKeyByFieldName[formKey];
		const contactField = crmContact[contactKey] as string | null;
		dispatch(setFormFieldByContactField(formKey, contactField));
	});
};

const retrieveSubFoldersContent = (itemId: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	try {
		const { driveList: { driveItems } } = getState();
		let itemMeta = driveItems[itemId];

		if (!itemMeta || (itemMeta && itemMeta.type === Api.DriveItemType.Directory)) {
			itemMeta = await dispatch(getOrFetchDriveItemMeta(itemId)) as DriveItemInfo;
		}

		const isFolder = itemMeta.type === Api.DriveItemType.Directory;

		if (isFolder) {
			const { resources } = itemMeta;
			if (resources) {
				for (let i = 0; i < resources.length; i++) {
					await dispatch(retrieveSubFoldersContent(resources[i].id));
				}
			}
		} else {
			dispatch(addSubFoldersContent(itemMeta));
		}
	} catch (error) {
		dispatch({ type: SharePanelActionKeys.FINISH_PROCESSING });
	}
};

export {
	SharePanelAction, SharePanelActionKeys, ShareOrigin, shareItems, setFormFieldsByContact, setCrmContacts,
	initiateShare, retrieveSubFoldersContent, share, finishShare, contactKeyByFieldName
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Share/actions.ts